import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, ENTITY_TYPE_LABELS, entityRenewal } from "@/lib/api";
import { useHousehold } from "@/context/HouseholdContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Flag, CalendarBlank, LinkSimple, UploadSimple, FilePdf, Trash, ArrowSquareOut, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const DOC_SLOTS = [
  { key: "ein", label: "EIN Confirmation (CP 575)", hint: "IRS Employer Identification Number letter" },
  { key: "articles", label: "Articles of Incorporation", hint: "Formation / organization document" },
];

export default function EntityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hid, isOperator, ownerLabel, refreshEntities } = useHousehold();
  const [ent, setEnt] = useState(null);
  const [form, setForm] = useState({ formation_date: "", renewal_date: "", sos_renewal_url: "" });
  const [saving, setSaving] = useState(false);
  const fileRefs = { ein: useRef(), articles: useRef() };

  const load = useCallback(async () => {
    if (!hid || !id) return;
    try {
      const r = await api.get(`/entities/${id}?household_id=${hid}`);
      setEnt(r.data);
      setForm({
        formation_date: r.data.formation_date || "",
        renewal_date: r.data.renewal_date || "",
        sos_renewal_url: r.data.sos_renewal_url || "",
      });
    } catch (e) { toast.error("Entity not found"); navigate("/entities"); }
  }, [hid, id, navigate]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/entities/${id}?household_id=${hid}`, {
        name: ent.name, type: ent.type, owner_party: ent.owner_party, notes: ent.notes || "",
        formation_date: form.formation_date || null,
        renewal_date: form.renewal_date || null,
        sos_renewal_url: form.sos_renewal_url || "",
      });
      toast.success("Saved");
      load(); refreshEntities();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const uploadDoc = async (slot, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/entities/${id}/documents/${slot}?household_id=${hid}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      if (fileRefs[slot].current) fileRefs[slot].current.value = "";
      load();
    } catch (e) { toast.error("Upload failed"); }
  };

  const viewDoc = async (slot) => {
    try {
      const r = await api.get(`/entities/${id}/documents/${slot}?household_id=${hid}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("No document on file"); }
  };

  const deleteDoc = async (slot) => {
    if (!window.confirm("Remove this document?")) return;
    await api.delete(`/entities/${id}/documents/${slot}?household_id=${hid}`);
    toast.success("Document removed"); load();
  };

  if (!ent) return <div className="text-sm text-muted-foreground">Loading entity…</div>;

  const rn = entityRenewal(form.renewal_date, ent.type);
  const fmtDate = (dt) => dt ? dt.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }) : "—";

  return (
    <div className="space-y-6 animate-fade-up max-w-4xl">
      <button data-testid="entity-back-button" onClick={() => navigate("/entities")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Back to Entities
      </button>

      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">{ENTITY_TYPE_LABELS[ent.type] || ent.type} · {ownerLabel(ent.owner_party)}</p>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1 flex items-center gap-3">
            {rn.due && <span data-testid="entity-detail-alert-dot" className="h-3 w-3 rounded-full bg-destructive shrink-0" />}
            {ent.name}
          </h1>
        </div>
        {isOperator && (
          <Button data-testid="save-entity-detail-button" onClick={save} disabled={saving} className="rounded-sm font-semibold">
            {saving ? "Saving…" : "Save Changes"}
          </Button>
        )}
      </div>

      {/* Renewal flag banner */}
      <div
        data-testid="entity-renewal-banner"
        className={`rounded-sm border p-5 flex items-center gap-4 ${
          rn.due ? "border-destructive/40 bg-destructive/10" : rn.has ? "border-border bg-card" : "border-dashed border-border bg-card"
        }`}
      >
        <Flag size={28} weight={rn.due ? "fill" : "regular"} className={rn.due ? "text-destructive" : "text-muted-foreground/60"} />
        <div className="flex-1">
          <p className={`font-semibold ${rn.due ? "text-destructive" : rn.has ? "text-foreground" : "text-muted-foreground"}`}>
            {rn.missing ? "No renewal date — this LLC isn't being tracked"
              : rn.due ? `Renewal due in ${rn.days} day${rn.days === 1 ? "" : "s"}`
              : rn.has ? "Renewal scheduled" : "No renewal date set"}
          </p>
          <p className={`text-sm mt-0.5 ${rn.due ? "text-destructive/80" : "text-muted-foreground/70"}`}>
            {rn.missing ? "Add the annual renewal date below so it's flagged 30 days ahead each year."
              : rn.has ? `Next renewal: ${fmtDate(rn.next)}${!rn.due ? ` · in ${rn.days} days` : ""}`
              : "Add an annual renewal date to get reminders 30 days ahead."}
          </p>
        </div>
        {rn.has && form.sos_renewal_url && (
          <a data-testid="sos-renewal-link" href={form.sos_renewal_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
            Renew now <ArrowSquareOut size={15} />
          </a>
        )}
      </div>

      {/* Key dates + SOS link */}
      <div className="bg-card border border-border rounded-sm p-6 space-y-5">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Registration</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5"><CalendarBlank size={14} /> Creation date</Label>
            <Input data-testid="formation-date-input" type="date" disabled={!isOperator} value={form.formation_date} onChange={(e) => setForm({ ...form, formation_date: e.target.value })} className="rounded-sm mt-1.5" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5"><Flag size={14} /> Annual renewal date</Label>
            <Input data-testid="renewal-date-input" type="date" disabled={!isOperator} value={form.renewal_date} onChange={(e) => setForm({ ...form, renewal_date: e.target.value })} className="rounded-sm mt-1.5" />
            <p className="text-[11px] text-muted-foreground mt-1">Recurs every year. Flag lights up 30 days before.</p>
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5"><LinkSimple size={14} /> Secretary of State renewal page</Label>
          <div className="flex items-center gap-2 mt-1.5">
            <Input data-testid="sos-url-input" disabled={!isOperator} value={form.sos_renewal_url} onChange={(e) => setForm({ ...form, sos_renewal_url: e.target.value })} placeholder="https://..." className="rounded-sm font-mono text-sm" />
            {form.sos_renewal_url && (
              <a data-testid="sos-url-open" href={form.sos_renewal_url} target="_blank" rel="noreferrer" className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-sm border border-border text-sm font-semibold hover:bg-muted/60">
                Open <ArrowSquareOut size={14} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="bg-card border border-border rounded-sm p-6 space-y-4">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Documents</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {DOC_SLOTS.map((s) => {
            const doc = ent.documents?.[s.key];
            return (
              <div key={s.key} data-testid={`doc-slot-${s.key}`} className="border border-border rounded-sm p-4">
                <div className="flex items-center gap-2">
                  <FilePdf size={18} weight={doc ? "fill" : "regular"} className={doc ? "text-success" : "text-muted-foreground"} />
                  <p className="font-semibold text-sm">{s.label}</p>
                  {doc && <CheckCircle size={15} weight="fill" className="text-success ml-auto" />}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{s.hint}</p>
                {doc ? (
                  <div className="mt-3 flex items-center gap-2">
                    <button data-testid={`view-doc-${s.key}`} onClick={() => viewDoc(s.key)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline truncate">
                      <FilePdf size={15} weight="fill" /> <span className="truncate">{doc.filename || "View"}</span>
                    </button>
                    {isOperator && (
                      <button data-testid={`delete-doc-${s.key}`} onClick={() => deleteDoc(s.key)} className="ml-auto text-muted-foreground hover:text-destructive shrink-0"><Trash size={15} /></button>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground/70 mt-3">No file uploaded</p>
                )}
                {isOperator && (
                  <>
                    <input ref={fileRefs[s.key]} type="file" accept="application/pdf,image/*" hidden onChange={(e) => uploadDoc(s.key, e.target.files?.[0])} data-testid={`upload-input-${s.key}`} />
                    <Button data-testid={`upload-doc-${s.key}`} size="sm" variant="outline" onClick={() => fileRefs[s.key].current?.click()} className="rounded-sm mt-3">
                      <UploadSimple size={14} className="mr-1" /> {doc ? "Replace" : "Upload"}
                    </Button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
